<template>
  <div class="container-fluid px-4">
    <BaseTable :rows="assignments" :columns="columns" searchable :selectable="false" :loading="loading"
      :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
      @searchChanged="onSearchChanged">
      <template #buttons>
        <MaterialButton size="sm" variant="success" @click="viewPersonsNextAssignment">
          View User's next assignment(s)
        </MaterialButton>
        <label class="ms-3">
          *Note: interest skills are skills that have not yet been assigned, the person has only expressed
          interest in serving with the skill.
        </label>
      </template>

      <!-- ✅ actions slot -->
      <template #actions="{ row }">
        <i class="material-icons text-lg text-danger cursor-pointer d-flex justify-content-center my-0 py-0"
          @click="undecline(row)">
          close
        </i>
      </template>
    </BaseTable>

    <!-- Modal for displaying schedule -->
    <Modal v-if="isModalOpen" :modalHeading="modalHeading" :iframeUrl="iframeUrl" size="xl" @close="closeModal" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from "vue";
import { useStore } from "vuex";
import BaseTable from "@/components/Table/Table.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import Modal from "@/components/Modal/Modal.vue";
import { useRoute } from "vue-router";
import { getLocationId } from "../../../utils/SessionDataHandler";
import { baseUrl } from "@/utils/BaseUrl.js";

const store = useStore();
const route = useRoute();

const loading = ref(true);
const searchTerm = ref("");
const _injectedPersonId = inject('editUserPersonId', null);
const personId = _injectedPersonId?.value || Number(route.params.userid);
const locationId = getLocationId();

// Modal state
const isModalOpen = ref(false);
const modalHeading = ref("User's Next Assignment(s)");
const iframeUrl = ref("");

// ✅ pagination state from Vuex
const pagination = computed(() => store.getters.personAssignmentsPagination || {});

// ✅ computed assignments (with search filtering applied)
const assignments = computed(() => {
  const response = store.getters.allAssignments || {};
  console.log("response", response);

  const data = response || {};

  // const upcoming = (data.upcoming_assignments || []).map((a) => ({
  //   id: a.id,
  //   workgroupName: a.workgroup_name,
  //   activity: `${a.activity_name} -> ${a.start_date}`,
  //   declineDate: "-",
  //   responseMethod: a.response_method,
  //   status: "Upcoming",
  // }));

  const declined = (data.declined_assignments || []).map((a) => ({
    id: a.id,
    workgroupName: a.workgroup_name,
    activity: `${a.activity_name} -> ${a.start_date}`,
    declineDate: a.decline_date ?? "-",
    responseMethod: a.response_method,
    status: "Declined",
  }));

  // const allAssignments = [...upcoming, ...declined];

  const allAssignments = declined;

  if (!searchTerm.value) return allAssignments;

  const term = searchTerm.value.toLowerCase();
  return allAssignments.filter(
    (a) =>
      a.workgroupName?.toLowerCase().includes(term) ||
      a.activity?.toLowerCase().includes(term) ||
      a.status?.toLowerCase().includes(term)
  );
});

// ✅ table columns
const columns = [
  { key: "id", label: "ID" },
  { key: "workgroupName", label: "Workgroup Name" },
  { key: "activity", label: "Activity Name -> Date" },
  { key: "declineDate", label: "Decline Date" },
  { key: "responseMethod", label: "Response Method" },
  { key: "status", label: "Status" },
];

// --------------------------------------------------
// 🔹 Fetch with pagination and search
// --------------------------------------------------
const fetchAssignments = async (page = 1, pageSize = pagination.value.pageSize ?? 10) => {
  loading.value = true;
  try {
    await store.dispatch("fetchPersonAssignments", {
      personId,
      locationId,
      pageNumber: page,
      pageSize,
      search: searchTerm.value,
    });
  } finally {
    loading.value = false;
  }
};

// --------------------------------------------------
// 🔹 Pagination / Search Handlers
// --------------------------------------------------
const onChangePage = async (page) => {
  await fetchAssignments(page, pagination.value.pageSize);
};

const onUpdatePageSize = async (pageSize) => {
  await fetchAssignments(1, pageSize);
};

const onSearchChanged = async (term) => {
  searchTerm.value = term;
  await fetchAssignments(1, pagination.value.pageSize ?? 10);
};

// --------------------------------------------------
// 🔹 Other Actions
// --------------------------------------------------
const undecline = async (row) => {
  try {
    await store.dispatch("undeclineWorkgroup", {
      personId,
      aiwgId: row.id,
      locationId,
    });
    await fetchAssignments(pagination.value.currentPage ?? 1, pagination.value.pageSize ?? 10);
  } catch (err) {
    console.error("Failed to undecline", err.message);
  }
};

const viewPersonsNextAssignment = async () => {
  const scheduleUrl = store.getters.allAssignments?.schedule_url;
  if (scheduleUrl) {
    // schedule_url from backend is a relative path (e.g. /MF/cs-mf-personschedule/?personid=123)
    // Prepend the environment-specific base URL to avoid hardcoded dev domain
    iframeUrl.value = scheduleUrl.startsWith('http') ? scheduleUrl : `${baseUrl}${scheduleUrl}`;
    modalHeading.value = "User's Next Assignment(s)";
    isModalOpen.value = true;
  } else {
    console.error("Schedule URL not found");
  }
};

const closeModal = () => {
  isModalOpen.value = false;
  iframeUrl.value = "";
};

// --------------------------------------------------
// 🔹 Lifecycle
// --------------------------------------------------
onMounted(async () => {
  await fetchAssignments();
});
</script>